import {
  WSAlert,
  WSChip,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSInfiniteScroll,
  WSList,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { Route, Switch, useHistory } from "react-router-dom";
import { WSQueries } from "../../../query/WSQuery";
import { useAddClientModal } from "../components/AddClientModal/useAddClientModal";
import { ClientsTable } from "../components/ClientsTable";

import { useCallback } from "react";
import { useQueryPayerRows } from "../../../query/search/payer/queries/useQueryPayerRows";
import { useQueryPayerRowsSummary } from "../../../query/search/payer/queries/useQueryPayerRowsSummary";
import {
  PayerPayeeEngagementSearchableStatus,
  PayerRowsQuery
} from "../../../services/search";
import { useUrlQueryFilters } from "../../../utils/router";

import { PayerPayeeStatus } from "@wingspanhq/payments/dist/interfaces";
import { Tabs } from "../../../components/Tabs";
import { KEYBOARD_SHORTCUT_SEARCH } from "../../../constants/keyboardShortcuts";
import { useUserId } from "../../../query/hooks/helpers";
import { useRequirementsListQuery } from "../../../query/requirements/queries/useRequirementsListQuery";
import { useActivities } from "../../../query/users/queries";
import { plural } from "../../../shared/utils/plural";
import { useOpenModalBySearchParam } from "../../../utils/useOpenModalBySearchParam";
import { useRedirectToByKeyPress } from "../../../utils/useRedirectToByKeyPress";
import { useCustomization } from "../../customization";
import { selectorExpiredRequirements } from "../../Requirements/selectors/selectorExpiredRequirements";
import { selectorExpiringRequirements } from "../../Requirements/selectors/selectorExpiringRequirements";
import { selectorIsAllRequirementsComplete } from "../../Requirements/selectors/selectorIsAllRequirementsComplete";
import { PAYERS_PATH, PAYERS_REQUIREMENTS_PATH } from "../paths";
import { usePayerRequirements } from "../../Requirements/modules/custom/payer/usePayerRequirements";
import { RouteClientsRequirements } from "./RouteClientsRequirements";
import styles from "./styles.module.scss";

type FiltersValues = {
  searchableStatus?:
    | PayerPayeeEngagementSearchableStatus
    | PayerPayeeEngagementSearchableStatus[];
};

const DEFAULT_FILTERS = {
  "payeeOwnedData.status": PayerPayeeStatus.Active
};

export const RouteClients: React.FC = () => {
  const { terminology } = useCustomization();
  const { filters, setFilters, changedFilters } =
    useUrlQueryFilters<PayerRowsQuery>({
      filter: DEFAULT_FILTERS,
      sort: {
        updatedAt: "desc"
      }
    });
  const history = useHistory();
  const userId = useUserId();
  const addClientModal = useAddClientModal();
  useOpenModalBySearchParam("add_client", addClientModal);

  const queryPayerRows = useQueryPayerRows(filters);
  const queryPayerRowsSummary = useQueryPayerRowsSummary(filters);
  const queryActivity = useActivities(userId);
  const queryRequirements = useRequirementsListQuery({
    filter: {
      payeeId: userId,
      payerPayeeEngagementIds: {
        "!=": "null"
      }
    },
    sort: {
      updatedAt: "desc"
    }
  });

  const onAddClient = useCallback(async () => {
    const clientResponse = await addClientModal.open();

    if (clientResponse) {
      history.push(`${PAYERS_PATH}/${clientResponse.payerId}`);
    }
  }, [addClientModal, history]);

  // const values = useMemo(() => {
  //   return queryParamsToFiltersValues(filters.filter || {});
  // }, [filters.filter]);

  const activity = queryActivity.data;

  useRedirectToByKeyPress(KEYBOARD_SHORTCUT_SEARCH, `${PAYERS_PATH}/search`);

  const requirements = usePayerRequirements(queryRequirements.data || []);

  const isRequirementsComplete =
    selectorIsAllRequirementsComplete(requirements);

  const expiringRequirements = selectorExpiringRequirements(
    queryRequirements.data || []
  );

  const expiredRequirements = selectorExpiredRequirements(
    queryRequirements.data || []
  );

  return (
    <WSPage>
      <WSPageToolbar
        title={plural(terminology().getPaidClient)}
        actions={[
          {
            label: "Search",
            icon: "search",
            buttonKind: "Tertiary",
            hideButtonOn: false,
            buttonOrder: 0,
            onClick: () => history.push(`${PAYERS_PATH}/search`)
          },
          {
            label: "Add client",
            icon: "plus",
            buttonKind: "Primary",
            onClick: onAddClient
          },
          activity?.flows?.personalPayLinkSetup?.complete
            ? {
                icon: "link",
                label: "View custom pay link",
                onClick: () => {
                  history.push("/member/personal-pay-link/dashboard");
                }
              }
            : {
                icon: "link",
                label: "Get a custom pay link",
                helpText: "Click to learn more",
                onClick: () => {
                  history.push("/member/personal-pay-link/overview");
                }
              },
          {
            icon: "archive",
            label: "View archived clients",
            onClick: () => {
              history.push(`${PAYERS_PATH}/archived`);
            }
          }
        ]}
      />
      {(expiringRequirements.length > 0 || expiredRequirements.length > 0) && (
        <WSList mb="L">
          {expiredRequirements.length > 0 && (
            <WSAlert
              theme="warning"
              icon="alert"
              title="Requirements incomplete"
              action={{
                text: "View requirements",
                onClick: () => {
                  history.push(PAYERS_REQUIREMENTS_PATH);
                }
              }}
            >
              You must complete all eligibility requirements for an engagement
              before you can receive payments for work under that engagement.
            </WSAlert>
          )}

          {expiringRequirements.length > 0 && (
            <WSAlert
              theme="neutral"
              icon="calendar"
              title="Requirements expiring soon"
              action={{
                text: "View requirements",
                onClick: () => {
                  history.push(PAYERS_REQUIREMENTS_PATH);
                }
              }}
            >
              You have {expiringRequirements.length} requirements expiring soon.
            </WSAlert>
          )}
        </WSList>
      )}

      {requirements.length ? (
        <Tabs
          mb="L"
          tabs={[
            {
              label: "Clients",
              path: PAYERS_PATH
            },
            {
              label: "Requirements",
              path: PAYERS_REQUIREMENTS_PATH,
              pill: {
                icon: true,
                theme: isRequirementsComplete ? "success" : "warning"
              }
            }
          ]}
        />
      ) : null}
      <Switch>
        <Route
          path={PAYERS_PATH}
          exact
          render={() => (
            <>
              <WSQueries
                queries={{
                  queryPayerRows,
                  queryPayerRowsSummary
                }}
              >
                {({ queryPayerRowsData, queryPayerRowsSummaryData }) => {
                  return (
                    <>
                      {/* <WSFilters<FiltersValues>
              mt="L"
              mb="XL"
              values={values}
              onFilter={newValues => {
                setFilters({
                  filter: filtersValuesToQueryParams(newValues)
                });
              }}
              info={{
                count: queryPayerRowsSummaryData.listSize
              }}
            /> */}
                      <WSFlexBox.CenterY
                        mb="L"
                        className={styles.statsContainer}
                      >
                        <WSElement className={styles.resultsStats}>
                          <WSText.ParagraphXs color="gray500">
                            Showing:
                          </WSText.ParagraphXs>
                          <WSChip
                            pillText={`${
                              queryPayerRowsSummaryData.listSize
                            } item${
                              queryPayerRowsSummaryData.listSize === 1
                                ? ""
                                : "s"
                            }`}
                            theme="neutral"
                            pillType="noBackground"
                          />
                        </WSElement>
                      </WSFlexBox.CenterY>
                      {Object.keys(changedFilters).length &&
                      queryPayerRowsData.length === 0 ? (
                        <WSEmptyState
                          hasBorder
                          bgColor="gray"
                          type="search"
                          title="No results found"
                          description="Try adjusting your search or filter to find what you’re looking for."
                        />
                      ) : queryPayerRowsData.length === 0 ? (
                        <WSEmptyState
                          hasBorder
                          bgColor="gray"
                          type="people"
                          title="No clients yet"
                          description="Once a client is added, they will appear here."
                          actions={{
                            buttons: [
                              {
                                label: "Add client",
                                kind: "Secondary",
                                onClick: onAddClient
                              }
                            ]
                          }}
                        />
                      ) : (
                        <WSInfiniteScroll
                          onLoad={() => {
                            queryPayerRows.fetchNextPage();
                          }}
                          loadMore={
                            queryPayerRows.data &&
                            queryPayerRows.data.length > 0
                          }
                          endOfList={!queryPayerRows.hasNextPage}
                          loading={!!queryPayerRows.isFetchingNextPage}
                        >
                          <ClientsTable
                            listPath={PAYERS_PATH}
                            clients={queryPayerRowsData}
                          />
                        </WSInfiniteScroll>
                      )}
                    </>
                  );
                }}
              </WSQueries>
            </>
          )}
        />
        <Route
          render={() => (
            <RouteClientsRequirements
              isLoading={queryRequirements.isFetching}
              requirements={requirements}
            />
          )}
          path={PAYERS_REQUIREMENTS_PATH}
          exact
        />
      </Switch>
    </WSPage>
  );
};
